/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react'
import FormLabel from '@mui/material/FormLabel'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import Chip from '@mui/material/Chip'
import { keyBy } from 'lodash'
import { FormHelperText } from '@mui/material'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import * as classes from './styles'

type Option = {
  id: string
  label: string
}

export type Props = {
  options: Option[]
  value: string | string[]
  isMultiSelect?: boolean
  label?: string
  error?: string
  onChange: (value: string | string[]) => void
}

export const ChipGroup = forwardRef(({ options, value, isMultiSelect, label, error, onChange }: Props, ref) => {
  const selected = isMultiSelect ? keyBy(value) : { [value as string]: value }

  const handleChange = (optionId: string) => {
    if (isMultiSelect) {
      const currentSelectedIds = typeof value === 'string' ? [value] : value

      const newSelectedIds = selected[optionId]
        ? currentSelectedIds.filter((id) => id !== optionId)
        : [...currentSelectedIds, optionId]

      onChange(newSelectedIds)
      return
    }

    onChange(optionId)
  }

  return (
    <FormControl component="fieldset" variant="standard" error={!!error}>
      {label && <FormLabel component="legend">{label}</FormLabel>}
      <FormGroup css={classes.formGroup} ref={ref}>
        {options.map((option) => (
          <Chip
            key={option.id}
            css={classes.chip(!!selected[option.id])}
            onClick={() => handleChange(option.id)}
            label={
              <>
                {option.label} {selected[option.id] && <CheckIcon />}
              </>
            }
            color="primary"
          />
        ))}
      </FormGroup>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  )
})

ChipGroup.displayName = 'ChipGroup'
